import axios from "axios";
import { fileTypeFromBuffer } from "file-type";
import { uploadResourceToAWS } from "shared-ui/EducationResource/uploadUtils";
import { useState } from "react";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import { environment } from "apps/web/src/environment";

const ERROR_MESSAGE_UPLOADER_INIT =
  "There is an issue with the file uploader. Please refresh the page and try again. If the issue persists, contact customer support.";

interface useUploadResourceProps {
  onComplete: (resourceUrl: string) => any | Promise<any>;
}

export const useUploadResource = ({ onComplete }: useUploadResourceProps): [(url: string) => Promise<void>, boolean] => {
  const { setMessage } = useSnackbar();
  const uploadUrl = `${environment.VITE_URL_SCHEME}${environment.VITE_SERVER_DOMAIN}/upload-education-resource`;
  if (!uploadUrl) {
    setMessage("error", ERROR_MESSAGE_UPLOADER_INIT);
  }

  const [resourceUploading, setResourceUploading] = useState(false);
  const uploadResource = async (fileUrl: string) => {
    setResourceUploading(true);

    try {
      // Convert the base64 data to binary and determine the file type
      const base64String = fileUrl.replace(/^data:.+;base64,/, "");
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const fileType = await fileTypeFromBuffer(bytes);

      // Throw error if the file type isn't recognized or is a video
      if (!fileType || fileType.mime.startsWith("video/")) {
        setMessage("error", "Unsupported file type");
        setResourceUploading(false);
        return;
      }

      const res = await axios.get(uploadUrl, {
        params: {
          fileExt: fileType.ext,
          fileMime: fileType.mime,
        },
        withCredentials: true,
      });

      const toUpload = await uploadResourceToAWS({ resData: res.data, fileBuffer: bytes, fileType });

      await onComplete(toUpload);
      setResourceUploading(false);
    } catch (e) {
      console.error({
        type: "misc",
        message: (e as Error).message,
      });
      setMessage("error", "Something went wrong!");
      setResourceUploading(false);
    }
  };

  return [uploadResource, resourceUploading];
};
